import React, {cloneElement, useState} from 'react';
import { useOnClickOutside } from "./useOnClickOutside.js";

// utilities
import {capitalizeSentence, formatCurrency} from './utilities.js';

// kendo react data grid custom column filter
import {GridColumnMenuFilter, GridColumnMenuSort} from "@progress/kendo-react-grid";
import {Popover, Tooltip} from "@progress/kendo-react-tooltip";

// kendo react custom column menu
export const ColumnMenu = props => {
    return <div className={"ksm-column-menu"}>
        <GridColumnMenuSort {...props} />
        <GridColumnMenuFilter
            {...props}
            hideSecondFilter={true}
            expanded={true}
        />
    </div>;
};

//Kendo react custom column menu with sort only
export const ColumnSortOnlyMenu = props => {
    return <div className={"ksm-column-menu"}>
        <GridColumnMenuSort {...props} />
    </div>;
};

// kendo react custom disable row render
export const DisableRowRender = (tr, props) => {
    if (props.dataItem.disabled) {
        return cloneElement(
            tr,
            {...tr.props, className: 'k-state-disabled', style: {pointerEvents: 'auto', opacity: '.7'}},
            tr.props.children
        );
    }
    return tr
};

// text align middle cell
export const TextAlignMiddleCell = (props) => {
    const field = props.field;
    return (
        <td
            style={{
                textAlign: 'center',
                verticalAlign: 'middle'
            }}
        >
            {props.dataItem[field]}
        </td>
    );
}

// Capitalize the first letter of every word cell
export const CapitalLettersCell = (props) => {
    const field = props.field;
    return (
        <td className={'capital-letters'}>
            {props.dataItem[field]}
        </td>
    );
}

// convert date/string to into KSM date format (e.g. March 9, 2024)
export const convertDateFormat = (date) => {
    if (date === null || date === undefined || date === '' || date.toString() === "Invalid Date") {
        return null;
    }

    date = new Date(date);

    const options = {month: 'long', day: 'numeric', year: 'numeric'};
    return new Intl.DateTimeFormat('en-US', options).format(date);
}

// formatted date cell
export const DateCell = (props) => {
    const field = props.field;
    return (
        <td>
            {convertDateFormat(props.dataItem[field])}
        </td>
    );
}

// formatted date cell that turns text red if expired
export const DateIsExpiredCell = (props) => {
    const field = props.field;
    let currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    return (
        (new Date(convertDateFormat(currentDate)) <= new Date(convertDateFormat(props.dataItem[field]))) ?
            <td>
                {convertDateFormat(props.dataItem[field])}
            </td>
            :
            <td
                style={{
                    color: "var(--color-alert-danger-base)",
                }}
            >
                {convertDateFormat(props.dataItem[field])}
            </td>
    );
}

// whitespace no wrap for a cell (noticed prod nums get wrapped on spec. chars. -+)
export const NoWrapCell = (props) => {
    return (
        <td
            style={{
                whiteSpace: "nowrap",
            }}
        >
            {props.dataItem[props.field]}
        </td>
    )
}

// TextCapitalizeCell :  convert any text to Capitalized text Ex : KEYSIGHT -> Keysight
export const TextCapitalizeCell = (props) => {
    return (
        <td
            style={{
                whiteSpace: "nowrap",
                textAlign: 'center',
                verticalAlign: 'middle'
            }}
        >
            {capitalizeSentence(props.dataItem[props.field] || '')}
        </td>
    )
}

export const CurrencyCell = (props) => {
    return (
        <td style={{
            textAlign: 'right',
        }}>
            {formatCurrency(props.dataItem[props.field],
                props.dataItem['currency_locale'], props.dataItem['currency_code'])}
        </td>
    )
}


export const TooltipHeaderCell = (props) => {
    return (
        <span className="k-cell-inner">
                <span className="k-link">
                    <Tooltip
                        anchorElement="target"
                        showCallout={true}
                        parentTitle={true}
                        openDelay={0}
                        position="top"
                    >
                    <span title={props.tooltip} className="k-column-title">
                        {props.title}
                    </span>
                        </Tooltip>
                </span>
            </span>
    )
}


export const LongDataCell = (props) => {
    let data = props.dataItem[props.field] || null;
    let dataSubstring = "";
    if (data && data?.length > 100) dataSubstring = data.substring(0, 100) + "...";
    const anchor = React.useRef(null);
    const popoverRef = React.useRef(null);
    const [show, setShow] = useState(false);
    return (
        <td>
            {dataSubstring ? (
                <>
                    <Popover
                        show={show}
                        anchor={anchor.current}
                        position={"top"}
                        animate={false}
                        callout={true}
                        collision={{
                          vertical: 'fit',
                          horizontal: 'fit',
                        }}
                    >
                        <div
                            ref={popoverRef}
                            style={{
                                cursor: "pointer",
                                width:'52rem',
                                maxWidth: '52rem',
                                position: 'relative',
                                padding: '0.5rem'
                            }}
                        >
                            <span
                                style={{
                                    position: 'absolute',
                                    top: '-0.625rem',
                                    right: '-0.625rem',
                                    fontSize: '1rem',
                                }}
                                onClick={() => setShow(false)}
                                aria-label="Close popover"
                                title="Close"
                            >
                                &#10005;
                            </span>
                            <div
                                style={{
                                    wordBreak: 'break-all',
                                    overflowWrap: 'anywhere',
                                }}
                            >
                                {data}
                            </div>
                        </div>
                    </Popover>
                    <div
                        ref={anchor}
                        onClick={() => setShow(!show)}
                        className={'host-hyperlink'}
                        style={{
                            cursor: "pointer",
                        }}
                    >
                        {dataSubstring}
                    </div>
                </>
            ) : (
                data
            )}
        </td>
    );
};


export const LongDataNoWrapCell = (props) => {
    let data = props.dataItem[props.field] || null;
    let dataSubstring = "";
    if (data && data.length > 100) dataSubstring = data.substring(0, 100) + "...";
    const anchor = React.useRef(null);
    const popoverRef = React.useRef(null);
    const [show, setShow] = useState(false);

    useOnClickOutside(popoverRef, () => {setShow(false)});
    return (
        <td>
            {dataSubstring ? (
                <>
                    <Popover
                        show={show}
                        anchor={anchor.current}
                        position={"top"}
                        animate={false}
                        callout={true}
                        collision={{
                          vertical: 'fit',
                          horizontal: 'fit',
                        }}
                    >
                        <div
                            ref={popoverRef}
                            style={{
                                cursor: "pointer",
                                width:'52rem',
                                maxWidth: '52rem',
                                position: 'relative',
                                padding: '0.5rem'
                            }}
                        >
                            <span
                                style={{
                                    position: 'absolute',
                                    top: '-0.625rem',
                                    right: '-0.625rem',
                                    fontSize: '1rem',
                                }}
                                onClick={() => setShow(false)}
                                aria-label="Close popover"
                                title="Close"
                            >
                                &#10005;
                            </span>
                            <div
                                style={{
                                    wordBreak: 'break-all',
                                    overflowWrap: 'anywhere',
                                }}
                            >
                                {data}
                            </div>
                        </div>
                    </Popover>
                    <div
                        ref={anchor}
                        onClick={() => setShow(!show)}
                        className={'host-hyperlink'}
                        style={{
                            cursor: "pointer",
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {dataSubstring}
                    </div>
                </>
            ) : (
                data
            )}
        </td>
    );
};